// src/components/tabs/TabFiles.jsx
// Mirrors relfiles.inc — list, upload, delete, update description of attached files

import React, { useState, useEffect, useRef } from 'react';
import { tabsApi } from '../../services/api';
import FirmaDigitalModal from '../modals/FirmaDigitalModal';
import { useTexts, TXT } from '../../context/TextContext';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const OFFICE_PROTOCOLS = {
  doc: 'ms-word', docx: 'ms-word',
  xls: 'ms-excel', xlsx: 'ms-excel',
  ppt: 'ms-powerpoint', pptx: 'ms-powerpoint',
};

function getOfficeProtocol(filename) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  return OFFICE_PROTOCOLS[ext] || null;
}

async function fetchFile(arcId, disposition, arcNombre) {
  const token = localStorage.getItem('whl_token');
  const res   = await fetch(`${API_URL}/tabs/files/${arcId}/${disposition}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Error al obtener archivo');
  const blob     = await res.blob();
  const blobUrl  = URL.createObjectURL(blob);
  const filename = arcNombre || res.headers.get('x-filename') || 'archivo';
  return { blobUrl, filename };
}

const FUN_FIRMA_DIGITAL = 41;

export default function TabFiles({ docId, onHide }) {
  const { t } = useTexts();
  const { showToast } = useToast();
  const { user } = useAuth();
  const canFirma = (user?.functions || []).includes(FUN_FIRMA_DIGITAL);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingDesc, setEditingDesc] = useState({}); // arcId -> new desc text
  const [versions, setVersions]       = useState({}); // arcId -> [] | null (null=loading)
  const [firmaModal, setFirmaModal]   = useState(null); // { arcId, arcNombre } | null
  const fileRef  = useRef();
  const descRef  = useRef();

  useEffect(() => { load(); }, [docId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await tabsApi.getFiles(docId);
      setData(res.data);
    } catch { setData({ canView: false, canEdit: false, canDelete: false, files: [] }); }
    finally { setLoading(false); }
  };

  const handleUpload = async () => {
    const file = fileRef.current?.files[0];
    if (!file) return;

    // Check if a file with the same name already exists
    const existing = data?.files?.find(f => f.name === file.name);
    if (existing) {
      const ok = window.confirm(
        `"${file.name}" (v${existing.version})\n` +
        t(TXT.UPLOAD_NEW_VERSION, '¿Subir una nueva versión?')
      );
      if (!ok) return;
    }

    setUploading(true);
    try {
      const form = new FormData();
      form.append('archivo', file);
      form.append('desarc', descRef.current?.value || '');
      if (existing) form.append('existingArcId', existing.id);
      await tabsApi.uploadFile(docId, form);
      fileRef.current.value = '';
      if (descRef.current) descRef.current.value = '';
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    } finally { setUploading(false); }
  };

  const handleDelete = async (arcId) => {
    if (!window.confirm(t(TXT.CONFIRM_DELETE, '¿Está seguro que desea eliminar este archivo?'))) return;
    try {
      await tabsApi.deleteFile(docId, arcId);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  const toggleVersions = async (arcId) => {
    if (arcId in versions) {
      setVersions(prev => { const n = {...prev}; delete n[arcId]; return n; });
      return;
    }
    setVersions(prev => ({ ...prev, [arcId]: null })); // null = loading
    try {
      const res = await tabsApi.getFileVersions(docId, arcId);
      setVersions(prev => ({ ...prev, [arcId]: res.data }));
    } catch {
      setVersions(prev => { const n = {...prev}; delete n[arcId]; return n; });
    }
  };

  const handleDescSave = async (arcId) => {
    try {
      await tabsApi.updateFileDesc(docId, arcId, editingDesc[arcId]);
      setEditingDesc(prev => { const n = {...prev}; delete n[arcId]; return n; });
      load();
    } catch (err) {
      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
    }
  };

  if (loading) return <div className="text-center p-3"><div className="spinner-border spinner-border-sm" /></div>;
  if (!data?.canView) { if (onHide) onHide(); return null; }

  return (
    <div className="container-fluid">
      {data.files.map(f => {
        const isEditing  = f.id in editingDesc;
        const showingVer = f.id in versions;
        return (
          <React.Fragment key={f.id}>
          <div className="row mt-2 align-items-center">
            {/* Action buttons */}
            <div className="col-2 d-flex gap-1">
              <button className="btn btn-light btn-sm" title={t(TXT.DOWNLOAD, 'Descargar')}
                onClick={async () => {
                  try {
                    const { blobUrl, filename } = await fetchFile(f.id, 'download', f.name);
                    const a = document.createElement('a');
                    a.href = blobUrl; a.download = filename; a.click();
                    URL.revokeObjectURL(blobUrl);
                  } catch { showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error'); }
                }}>
                <i className="fa-solid fa-download fa-1x" />
              </button>
              {canFirma && (
                <button className="btn btn-light btn-sm" title="Firma digital"
                  onClick={() => setFirmaModal({ arcId: f.id, arcNombre: f.name })}>
                  <i className="bi bi-pen fa-1x" />
                </button>
              )}
              {data.canDelete && (
                <button className="btn btn-light btn-sm" title={t(TXT.DELETE, 'Eliminar')}
                  onClick={() => handleDelete(f.id)}>
                  <i className="fa-solid fa-trash-can fa-1x" />
                </button>
              )}
              <button className="btn btn-light btn-sm" title={t(TXT.VIEW, 'Ver')}
                onClick={async () => {
                  const protocol = getOfficeProtocol(f.name);
                  if (protocol) {
                    const token = localStorage.getItem('whl_token');
                    const fileUrl = `${API_URL}/tabs/files/${f.id}/view?t=${token}`;
                    const officeUrl = `${protocol}:ofe|u|${fileUrl}`;
                    console.log('[Office open]', officeUrl);
                    const a = document.createElement('a');
                    a.href = officeUrl;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  } else {
                    try {
                      const { blobUrl } = await fetchFile(f.id, 'view');
                      window.open(blobUrl, '_blank');
                    } catch { showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error'); }
                  }
                }}>
                <i className="fa-solid fa-eye fa-1x" />
              </button>
              {f.hasVersions && (
                <button className="btn btn-light btn-sm" title={t(TXT.VERSIONS, 'Versiones anteriores')}
                  onClick={() => toggleVersions(f.id)}>
                  <i className={`fa-solid fa-code-merge fa-1x${showingVer ? ' color1' : ''}`} />
                </button>
              )}
            </div>

            {/* Description */}
            <div className="col-5 border-bottom d-flex align-items-center gap-1">
              {data.canEdit ? (
                <>
                  {!isEditing ? (
                    <>
                      <button className="btn btn-light btn-sm" title={t(TXT.UPDATE_DESC, 'Modificar descripción')}
                        onClick={() => setEditingDesc(prev => ({ ...prev, [f.id]: f.desc || '' }))}>
                        <i className="fa-solid fa-pen fa-1x" />
                      </button>
                      <span>{f.desc}</span>
                    </>
                  ) : (
                    <>
                      <input
                        className="form-control form-control-sm"
                        style={{ border: 'none', outline: 0, background: 'transparent' }}
                        value={editingDesc[f.id]}
                        onChange={e => setEditingDesc(prev => ({ ...prev, [f.id]: e.target.value }))}
                      />
                      <button className="btn btn-light btn-sm" title={t(TXT.SAVE, 'Guardar')} onClick={() => handleDescSave(f.id)}>
                        <i className="fa-solid fa-check fa-1x" />
                      </button>
                      <button className="btn btn-light btn-sm" title={t(TXT.CANCEL, 'Cancelar')}
                        onClick={() => setEditingDesc(prev => { const n = {...prev}; delete n[f.id]; return n; })}>
                        <i className="fa-solid fa-xmark fa-1x" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <strong>{f.desc}</strong>
              )}
            </div>

            {/* Filename */}
            <div className="col border-bottom">{f.name}</div>

            {/* Date */}
            <div className="col-auto border-bottom text-muted" style={{ minWidth: '90px', textAlign: 'right' }}>
              {f.date ? String(f.date).substring(0, 10) : ''}
            </div>
          </div>

            {/* Previous versions panel */}
            {showingVer && (
              <div className="row">
                <div className="col-2" />
                <div className="col-10">
                  {versions[f.id] === null ? (
                    <div className="text-muted small ps-2"><i className="fa-solid fa-spinner fa-spin" /> {t(TXT.LOAD_MORE, 'Cargando...')}</div>
                  ) : (
                    <table className="table table-sm mb-1" style={{ fontSize: '0.82rem' }}>
                      <tbody>
                        {versions[f.id].map(v => (
                          <tr key={v.id}>
                            <td className="text-muted" style={{ width: '2rem' }}>v{v.version}</td>
                            <td>{v.desc}</td>
                            <td className="text-muted">{v.name}</td>
                            <td className="text-muted text-end" style={{ minWidth: '90px' }}>
                              {v.date ? String(v.date).substring(0, 10) : ''}
                            </td>
                            <td style={{ width: '5rem' }}>
                              <button className="btn btn-light btn-sm me-1" title={t(TXT.DOWNLOAD, 'Descargar')}
                                onClick={async () => {
                                  try {
                                    const { blobUrl, filename } = await fetchFile(v.id, 'download', v.name);
                                    const a = document.createElement('a');
                                    a.href = blobUrl; a.download = filename; a.click();
                                    URL.revokeObjectURL(blobUrl);
                                  } catch { showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error'); }
                                }}>
                                <i className="fa-solid fa-download fa-1x" />
                              </button>
                              {data.canDelete && (
                                <button className="btn btn-light btn-sm me-1" title={t(TXT.DELETE, 'Eliminar')}
                                  onClick={async () => {
                                    if (!window.confirm(t(TXT.CONFIRM_DELETE, '¿Eliminar esta versión?'))) return;
                                    try {
                                      await tabsApi.deleteFile(docId, v.id);
                                      setVersions(prev => ({
                                        ...prev,
                                        [f.id]: prev[f.id].filter(x => x.id !== v.id),
                                      }));
                                    } catch (err) {
                                      showToast(err.response?.data?.error || t(TXT.TRANSACTION_ERROR, 'Error'), 'error');
                                    }
                                  }}>
                                  <i className="fa-solid fa-trash-can fa-1x" />
                                </button>
                              )}
                              <button className="btn btn-light btn-sm" title={t(TXT.VIEW, 'Ver')}
                                onClick={async () => {
                                  const protocol = getOfficeProtocol(v.name);
                                  if (protocol) {
                                    const token = localStorage.getItem('whl_token');
                                    const fileUrl = encodeURIComponent(`${API_URL}/tabs/files/${v.id}/view?t=${token}`);
                                    window.location.href = `${protocol}:ofe|u|${fileUrl}`;
                                  } else {
                                    try {
                                      const { blobUrl } = await fetchFile(v.id, 'view');
                                      window.open(blobUrl, '_blank');
                                    } catch { showToast(t(TXT.TRANSACTION_ERROR, 'Error'), 'error'); }
                                  }
                                }}>
                                <i className="fa-solid fa-eye fa-1x" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}

      {firmaModal && (
        <FirmaDigitalModal
          arcId={firmaModal.arcId}
          arcNombre={firmaModal.arcNombre}
          onClose={() => setFirmaModal(null)}
        />
      )}

      {/* Upload form */}
      {data.canEdit && (
        <div className="row mt-3 align-items-center">
          <div className="col-5">
            <label className="me-2 small">{t(TXT.FILE_LABEL, 'Archivo')}:</label>
            <input type="file" ref={fileRef} className="form-control form-control-sm d-inline-block" style={{ width: 'auto' }} />
          </div>
          <div className="col-4">
            <label className="me-2 small">{t(TXT.DESCRIPTION_LABEL, 'Descripción')}:</label>
            <input type="text" ref={descRef} className="form-control form-control-sm d-inline-block" style={{ width: 'auto' }} />
          </div>
          <div className="col-3">
            <button className="button color1" onClick={handleUpload} disabled={uploading}>
              <i className="fa-solid fa-check fa-1x color1" />
              &nbsp;{uploading
                ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                : t(TXT.UPLOAD, 'Subir')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
